import { PunchBeamPosition } from "./PunchBeamPosition";
import { PunchPerforation } from "./PunchPerforation";

export interface ProductChildRef {
	ProductId: number;
	PartNumber: string;
	ExtendedPartNumber: string | null;
	AreaAssetId: number | null;
}

/** Ancestor chain step. Depth 1 = direct parent. Ordered root-first. */
export interface ProductAncestorRef {
	Depth: number;
	ProductId: number;
	PartNumber: string;
	ExtendedPartNumber: string | null;
	AreaAssetId: number | null;
}

export type ProductArea =
	| "weld"
	| "saw"
	| "paint"
	| "punch"
	| "wax"
	| "galv-sawed"
	| "galv-punched"
	| "galv-welded"
	| null;

export type ProductAreaKey = Exclude<ProductArea, null>;

export const PRODUCT_AREA_LABEL: Record<ProductAreaKey, string> = {
	saw: "Saw",
	weld: "Weld",
	paint: "Paint",
	punch: "Punch",
	wax: "Wax",
	"galv-sawed": "Galv-Sawed",
	"galv-punched": "Galv-Punched",
	"galv-welded": "Galv-Welded",
};

export const ALL_PRODUCT_AREAS = Object.keys(
	PRODUCT_AREA_LABEL,
) as ProductAreaKey[];

// Galv material is processed offsite: catalog setup only, no line behind it
export const GALV_AREAS: ReadonlySet<ProductArea> = new Set([
	"galv-sawed",
	"galv-punched",
	"galv-welded",
]);

export const PRODUCT_AREA_ASSET_ID: Record<ProductAreaKey, number> = {
	weld: 10,
	saw: 20,
	wax: 30,
	paint: 40,
	punch: 50,
	"galv-sawed": 70,
	"galv-punched": 80,
	"galv-welded": 90,
};

export const getProductArea = (
	p: Pick<Product, "AreaAssetId">,
): ProductArea => {
	const match = ALL_PRODUCT_AREAS.find(
		(area) => PRODUCT_AREA_ASSET_ID[area] === p.AreaAssetId,
	);
	return match ?? null;
};

// Valid parent areas per child area (process flow):
// Saw=root, Punch←Saw, Weld←Saw|Punch, Paint←Saw|Weld|Punch, Wax←any (terminal)
// Galv-* hangs off its in-house counterpart and is always terminal
export const VALID_PARENT_AREAS: Record<
	ProductAreaKey,
	Array<ProductAreaKey>
> = {
	saw: [],
	punch: ["saw"],
	weld: ["saw", "punch"],
	paint: ["saw", "weld", "punch"],
	wax: ["saw", "punch", "weld", "paint"],
	"galv-sawed": ["saw"],
	"galv-punched": ["punch"],
	"galv-welded": ["weld"],
};

export const canHaveParent = (area: ProductArea): boolean => {
	if (!area) return false;
	return VALID_PARENT_AREAS[area].length > 0;
};

export const isValidParentArea = (
	childArea: ProductArea,
	parentArea: ProductArea,
): boolean => {
	if (!childArea || !parentArea) return false;
	return VALID_PARENT_AREAS[childArea].includes(parentArea);
};

export interface Product {
	ProductId: number | null;
	PartNumber: string;
	ExtendedPartNumber: string | null;
	CustomerPartNumber: string | null;
	GenericName: string | null;
	CustomerId: number | null;
	CustomerName: string | null;
	SectionId: number | null;
	SectionName: string | null;
	GradeId: number | null;
	GradeName: string | null;
	IsActive: boolean;
	Comments: string | null;
	CreatedBy: string | null;
	CreatedDate: Date | null;
	ModifiedBy: string | null;
	ModifiedDate: Date | null;
	// Spec existence flags (own)
	HasWeldSpecs: boolean;
	HasSawSpecs: boolean;
	HasPaintSpecs: boolean;
	HasPunchSpecs: boolean;
	HasWaxSpecs: boolean;
	// Inherited spec flags (from ancestors)
	HasWeldSpecsInherited: boolean;
	HasSawSpecsInherited: boolean;
	HasPaintSpecsInherited: boolean;
	HasPunchSpecsInherited: boolean;
	HasWaxSpecsInherited: boolean;
	// Lineage
	ParentProductId: number | null;
	ParentPartNumber: string | null;
	ParentSawPartNumber: string | null;
	ParentSawCustomerPartNumber: string | null;
	ParentSawCutLength: number | null;
	ParentSawTopTolerance: number | null;
	ParentSawBottomTolerance: number | null;
	ParentWeldFinishedLength: number | null;
	Children: ProductChildRef[];
	Ancestors: ProductAncestorRef[];
	// FabricationSpecs
	AreaAssetId: number | null;
	CutLength: number | null;
	FinishedLength: number | null;
	Tolerance: string | null;
	Program: string | null;
	CleaningCycle: number | null;
	WebHoles: string | null; // Derived "Yes"/"No" from PunchSpecs presence in lineage
	BottomKeeper: string | null;
	BundleCol: number | null;
	BundleRow: number | null;
	BundleCount: number | null;
	StickSize: string | null;
	GroovedTimber: string | null;
	BandLength: number | null;
	// FinishSpecs (delivery attributes)
	Banded: boolean | null;
	NumberOfBands: number | null;
	Cardboard: boolean | null;
	WeldComments: string | null;
	// Drawing
	TestCycleInterval: number | null;
	DrawingPath: string | null;
	DrawingNumber: string | null;
	ClipId: number | null;
	WeldAuthor: string | null;
	WeldDateTimeModified: Date | null;
	// Clip
	ClipPartNumber: string | null;
	ClipWidth: number | null;
	ClipHeight: number | null;
	HolePattern: string | null;
	HoleGage: string | null;
	SideGage: string | null;
	// Paint / Punch
	PaintCutTypeId: number | null;
	PaintCutTypeName: string | null;
	SpecialInstructions: string | null;
	BandUp: boolean | null;
	// Catalog FKs
	EntryKeeperId: number | null;
	EntryKeeperCode: string | null;
	ExitKeeperId: number | null;
	ExitKeeperCode: string | null;
	// SawSpecs columns (from JOIN)
	TargetLength: number | null;
	TopTolerance: number | null;
	BottomTolerance: number | null;
	FinishPcB: number | null;
	MillLength: number | null;
	MillPcB: number | null;
	SawAuthor: string | null;
	SawDateTimeModified: Date | null;
	// PunchSpecs columns (from JOIN)
	UpperRiserDrawingNum: string | null;
	LowerRiserDrawingNum: string | null;
	StopBlockNumber: string | null;
	PunchAuthor: string | null;
	PunchDateTimeModified: Date | null;
	// Punch 1:many arrays
	BeamPositions: PunchBeamPosition[];
	Perforations: PunchPerforation[];
}

export const PRODUCT_INITIAL_VALUES: Product = {
	ProductId: null,
	PartNumber: "",
	ExtendedPartNumber: null,
	CustomerPartNumber: null,
	GenericName: null,
	CustomerId: null,
	CustomerName: null,
	SectionId: null,
	SectionName: null,
	GradeId: null,
	GradeName: null,
	IsActive: true,
	Comments: null,
	CreatedBy: null,
	CreatedDate: null,
	ModifiedBy: null,
	ModifiedDate: null,
	HasWeldSpecs: false,
	HasSawSpecs: false,
	HasPaintSpecs: false,
	HasPunchSpecs: false,
	HasWaxSpecs: false,
	HasWeldSpecsInherited: false,
	HasSawSpecsInherited: false,
	HasPaintSpecsInherited: false,
	HasPunchSpecsInherited: false,
	HasWaxSpecsInherited: false,
	ParentProductId: null,
	ParentPartNumber: null,
	ParentSawPartNumber: null,
	ParentSawCustomerPartNumber: null,
	ParentSawCutLength: null,
	ParentSawTopTolerance: null,
	ParentSawBottomTolerance: null,
	ParentWeldFinishedLength: null,
	Children: [],
	Ancestors: [],
	AreaAssetId: null,
	CutLength: null,
	FinishedLength: null,
	Tolerance: null,
	Program: null,
	CleaningCycle: null,
	WebHoles: null,
	BottomKeeper: null,
	BundleCol: null,
	BundleRow: null,
	BundleCount: null,
	StickSize: null,
	GroovedTimber: null,
	BandLength: null,
	Banded: null,
	NumberOfBands: null,
	Cardboard: null,
	WeldComments: null,
	TestCycleInterval: null,
	DrawingPath: null,
	DrawingNumber: null,
	ClipId: null,
	WeldAuthor: null,
	WeldDateTimeModified: null,
	ClipPartNumber: null,
	ClipWidth: null,
	ClipHeight: null,
	HolePattern: null,
	HoleGage: null,
	SideGage: null,
	PaintCutTypeId: null,
	PaintCutTypeName: null,
	SpecialInstructions: null,
	BandUp: null,
	EntryKeeperId: null,
	EntryKeeperCode: null,
	ExitKeeperId: null,
	ExitKeeperCode: null,
	TargetLength: null,
	TopTolerance: null,
	BottomTolerance: null,
	FinishPcB: null,
	MillLength: null,
	MillPcB: null,
	SawAuthor: null,
	SawDateTimeModified: null,
	UpperRiserDrawingNum: null,
	LowerRiserDrawingNum: null,
	StopBlockNumber: null,
	PunchAuthor: null,
	PunchDateTimeModified: null,
	BeamPositions: [],
	Perforations: [],
};
